import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { AgenciesService } from '../../../orval-gen/endpoints/agencies/agencies.service';
import { ConsumersService } from '../../../orval-gen/endpoints/consumers/consumers.service';
import {
  AgencyConsumerGet200Data,
  AgencyGet200Data,
  AgencyUpdateBody,
} from '../../../orval-gen/models';
import { skipErrorToast } from '../../interceptors/error.interceptor';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ErrorDirective } from '../../shared/directives/error.directive';
import { HelpersService } from '../../shared/services/helpers.service';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { unwrap } from '../../utils/http-utils';

type TabKey = 'agency-info' | 'branding';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'agency-info', label: 'Agency information' },
  { key: 'branding', label: 'Branding' },
];

const DEFAULT_BRAND_COLOR = '#A70000';

// The agency fields the form edits, as the hub names them.
type AgencyForm = Pick<
  AgencyGet200Data,
  | 'operational_email'
  | 'email_contact'
  | 'company_name'
  | 'street'
  | 'postal_code'
  | 'phone_contact'
  | 'city_name'
  | 'state_prov'
  | 'brand_color'
>;

@Component({
  selector: 'app-settings',
  imports: [ReactiveFormsModule, ErrorDirective, IconComponent],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  private readonly agenciesService = inject(AgenciesService);
  private readonly consumersService = inject(ConsumersService);
  private readonly toastr = inject(ToastrService);
  private readonly fb = inject(FormBuilder);
  private readonly helpersService = inject(HelpersService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly ls = inject(LocalStorageService);

  readonly tabs = TABS;
  readonly brandPresets = [DEFAULT_BRAND_COLOR, '#0B5CAD', '#0F766E', '#1E293B'];

  loaded = false;
  saving = false;
  accountForm!: FormGroup;
  consumerData: AgencyConsumerGet200Data | null = null;
  activeTab: TabKey = 'agency-info';
  imageURL: string | null = null;
  imageError = '';

  agencyActive = this.ls.getBool('agencyStatus');

  /** Values as last loaded/saved, used by "Discard". */
  private savedValue: Partial<AgencyForm> = {};

  ngOnInit(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab') as TabKey | null;
    this.activeTab = TABS.some(t => t.key === tab) ? tab! : 'agency-info';
    this.buildAccountForm();
    this.loadSettingsData();
  }

  setTab(tab: TabKey): void {
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  buildAccountForm(): void {
    this.accountForm = this.fb.group({
      // Required by the hub: every agency keeps an operational email, so it can be changed but not cleared.
      operational_email: ['', [Validators.required, Validators.email]],
      email_contact: ['', [Validators.required, Validators.email]],
      company_name: ['', Validators.required],
      street: ['', Validators.required],
      postal_code: ['', Validators.required],
      phone_contact: ['', Validators.required],
      city_name: [''],
      state_prov: [''],
      brand_color: [''],
    });
  }

  get brandColor(): string {
    return this.accountForm.get('brand_color')?.value || DEFAULT_BRAND_COLOR;
  }

  get agencyInitials(): string {
    const name = this.accountForm.get('company_name')?.value || this.ls.getItem('agency') || '';
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w: string) => w.charAt(0).toUpperCase())
      .join('');
  }

  setBrandColor(color: string): void {
    const control = this.accountForm.get('brand_color');
    control?.setValue(color);
    control?.markAsDirty();
  }

  onLogoSelected(event: Event): void {
    this.imageError = '';
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    input.value = '';

    // Errors show under the logo rather than as a toast.
    this.agenciesService
      .agencyUploadLogo({ logo: file }, { context: skipErrorToast() })
      .pipe(unwrap())
      .subscribe({
        next: agency => {
          this.imageURL = agency.logo_url;
          this.toastr.success('Logo uploaded.', 'Success');
        },
        error: err => {
          // A 413 from a proxy in front of the hub carries no JSON detail.
          this.imageError =
            err?.error?.detail ??
            (err?.status === 413 ? 'The logo is too large.' : 'Failed to upload logo.');
          console.error('Logo upload error:', err);
        },
      });
  }

  removeLogo(): void {
    this.agenciesService
      .agencyRemoveLogo({ context: skipErrorToast() })
      .pipe(unwrap())
      .subscribe({
        next: agency => {
          this.imageURL = agency.logo_url;
          this.imageError = '';
          this.toastr.success('Logo removed.', 'Success');
        },
        error: err => {
          this.imageError = err?.error?.detail ?? 'Failed to remove logo.';
          console.error('Logo removal error:', err);
        },
      });
  }

  loadSettingsData(): void {
    this.loaded = false;

    forkJoin({
      agency: this.agenciesService.agencyGet().pipe(
        unwrap(),
        catchError(error => {
          console.error('Agency error:', error);
          return of(null);
        })
      ),
      consumer: this.consumersService.agencyConsumerGet().pipe(
        unwrap(),
        catchError(error => {
          console.error('Consumer data error:', error);
          return of(null);
        })
      ),
    })
      .pipe(finalize(() => (this.loaded = true)))
      .subscribe(({ agency, consumer }) => {
        if (agency) {
          this.savedValue = {
            operational_email: agency.operational_email,
            email_contact: agency.email_contact,
            company_name: agency.company_name,
            street: agency.street,
            postal_code: agency.postal_code,
            phone_contact: agency.phone_contact,
            city_name: agency.city_name,
            state_prov: agency.state_prov,
            brand_color: agency.brand_color || DEFAULT_BRAND_COLOR,
          };
          this.accountForm.reset(this.savedValue);
          this.imageURL = agency.logo_url;
          this.agencyActive = agency.active;
        }

        this.consumerData = consumer;
      });
  }

  discardChanges(): void {
    this.accountForm.reset(this.savedValue);
  }

  saveChanges(): void {
    // The hub refuses an empty or malformed address for either email, so stop before sending one. The other required
    // fields are left to it, as it accepts them empty and older agencies may have none, which must not block
    // saving the branding.
    const emails = [
      this.accountForm.get('email_contact'),
      this.accountForm.get('operational_email'),
    ];
    if (emails.some(control => control?.invalid)) {
      emails.forEach(control => control?.markAsTouched());
      this.setTab('agency-info');
      this.toastr.error('Please correct the highlighted fields.', 'Validation Error');
      return;
    }

    const updatedData: AgencyForm = this.accountForm.getRawValue();
    const body: AgencyUpdateBody = {
      ...updatedData,
      // An empty colour restores the hub's default.
      brand_color: updatedData.brand_color || null,
    };
    this.saving = true;
    this.agenciesService
      .agencyUpdate(body, { context: skipErrorToast() })
      .pipe(
        unwrap(),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: agency => {
          this.savedValue = {
            ...updatedData,
            brand_color: agency.brand_color || DEFAULT_BRAND_COLOR,
          };
          this.accountForm.reset(this.savedValue);
          this.toastr.success('Your changes have been saved.', 'Success');
        },
        error: err => {
          if (this.helpersService.handleApiErrors(this.accountForm, err?.error)) {
            this.toastr.error('Please correct the highlighted fields.', 'Validation Error');
          } else {
            this.toastr.error(err?.error?.detail ?? 'Failed to save your changes.', 'Error');
          }
          console.error('Error saving agency:', err);
        },
      });
  }
}
